import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, UserCheck, AlertTriangle, Clock, Coins, Package, ArrowRight, UserPlus, Gift } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { PageLoader, LoadError } from "@/components/admin/LoadState";
import { usePageTitle } from "@/hooks/usePageTitle";
import { fmtNum, plural } from "@/lib/format";

type Card = { icon: any; label: string; value: number; help: string; color: string; perm?: string; to?: string; action?: string };

export default function AdminDashboard() {
  usePageTitle("Inicio");
  const { user } = useAuth();
  const [stats, setStats] = useState<any>(null);
  const [failed, setFailed] = useState(false);
  const [params, setParams] = useSearchParams();
  const days = params.get("dias") || "30";

  const load = () => {
    setFailed(false);
    api.get("/admin/stats", { params: { days } }).then((r) => setStats(r.data)).catch(() => setFailed(true));
  };
  useEffect(() => { load(); }, [days]); // eslint-disable-line

  if (failed) return <LoadError what="el resumen" onRetry={load} />;
  if (!stats) return <PageLoader />;

  const perms = user?.permissions || [];
  const tierColors: Record<string, string> = stats.tier_colors || {};
  const maxTier = Math.max(...Object.values(stats.tier_distribution as Record<string, number>), 1);

  // Lo que requiere acción va primero y lleva directo a la lista correspondiente.
  const todo: Card[] = [
    { icon: Clock, label: "Registros por aprobar", value: stats.pending_approvals ?? 0, color: "text-amber-700", perm: "users", to: "/admin/usuarios?tab=pending", action: "Revisar registros",
      help: "Personas que se registraron y esperan tu aprobación para usar el programa." },
    { icon: Package, label: "Canjes pendientes", value: stats.pending_redemptions, color: "text-gold-ink", perm: "redemptions", to: "/admin/canjes", action: "Ver canjes",
      help: "Premios que los clientes pidieron y esperan aprobación." },
  ].filter((c) => !c.perm || perms.includes(c.perm));

  const p = stats.period || {};
  const activity: Card[] = [
    { icon: UserPlus, label: "Registros nuevos", value: p.new_registrations ?? 0, color: "text-blue-700", perm: "users", to: "/admin/usuarios",
      help: "Personas que se registraron en el periodo." },
    { icon: Coins, label: "Puntos entregados", value: p.points_granted ?? 0, color: "text-gold-ink", perm: "users", to: "/admin/puntos/historial",
      help: "Puntos sumados a clientes: entregas del equipo, cargas por Excel, bonos y registros." },
    { icon: Gift, label: "Canjes solicitados", value: p.redemptions ?? 0, color: "text-emerald-700", perm: "redemptions", to: "/admin/canjes",
      help: "Premios pedidos en el periodo (sin contar los rechazados)." },
    { icon: Package, label: "Puntos canjeados", value: p.points_redeemed ?? 0, color: "text-emerald-700", perm: "redemptions",
      help: "Puntos que los clientes usaron en esos canjes." },
  ].filter((c) => !c.perm || perms.includes(c.perm));

  const summary: Card[] = [
    { icon: Users, label: "Clientes registrados", value: stats.total_users, color: "text-blue-700", perm: "users", to: "/admin/usuarios",
      help: "Todas las cuentas de clientes, incluidas las pendientes, rechazadas e inhabilitadas." },
    { icon: UserCheck, label: "Clientes al día", value: stats.active_users, color: "text-emerald-700", perm: "users",
      help: "Cuentas con estado “Al día”. Incluye registros que aún no se aprueban." },
    { icon: AlertTriangle, label: "Clientes no al día", value: stats.overdue_users, color: "text-amber-700", perm: "users",
      help: "Pueden entrar, pero ven un aviso de pagos pendientes." },
    { icon: Coins, label: "Puntos en saldos de clientes", value: stats.total_points_issued, color: "text-gold-ink", perm: "users",
      help: "Suma de los saldos actuales de todos los clientes (puntos que todavía pueden canjear)." },
  ].filter((c) => !c.perm || perms.includes(c.perm));

  return (
    <div className="space-y-8 animate-fade-in" data-testid="admin-dashboard">
      <div>
        <h1 className="text-3xl font-serif font-medium">Inicio</h1>
        <p className="text-sm text-slate-600 mt-1">Lo pendiente, la actividad del periodo que elijas y el estado actual de los clientes.</p>
      </div>

      {todo.length > 0 && (
        <section aria-labelledby="todo-title">
          <h2 id="todo-title" className="text-lg font-semibold mb-3">Pendiente</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {todo.map((c) => (
              <Link key={c.label} to={c.to!} data-testid={`dashboard-card-${c.perm}`} className="card-obsidian p-6 block hover:border-gold/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">{c.label}</span>
                  <c.icon className={`w-5 h-5 ${c.color}`} aria-hidden="true" />
                </div>
                <p className="text-3xl font-serif font-medium text-slate-900">{fmtNum(c.value)}</p>
                <p className="text-xs text-slate-600 mt-2">{c.help}</p>
                <p className="text-sm text-navy font-medium mt-3 inline-flex items-center gap-1">
                  {c.value > 0 ? c.action : "Nada pendiente"} {c.value > 0 && <ArrowRight className="w-4 h-4" aria-hidden="true" />}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {activity.length > 0 && (
        <section aria-labelledby="activity-title">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
            <h2 id="activity-title" className="text-lg font-semibold">Actividad</h2>
            <div className="flex items-center gap-2">
              <Label htmlFor="period-select" className="text-sm text-slate-700">Periodo</Label>
              <Select value={days} onValueChange={(v: string) => setParams(v === "30" ? {} : { dias: v }, { replace: true })}>
                <SelectTrigger id="period-select" data-testid="dashboard-period" className="w-44 bg-obsidian-card border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-obsidian-card border-slate-200 text-slate-900">
                  <SelectItem value="7">Últimos 7 días</SelectItem>
                  <SelectItem value="30">Últimos 30 días</SelectItem>
                  <SelectItem value="90">Últimos 90 días</SelectItem>
                  <SelectItem value="365">Último año</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {activity.map((c) => {
              const body = (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">{c.label}</span>
                    <c.icon className={`w-5 h-5 ${c.color}`} aria-hidden="true" />
                  </div>
                  <p className="text-3xl font-serif font-medium text-slate-900">{fmtNum(c.value)}</p>
                  <p className="text-xs text-slate-600 mt-2">{c.help}</p>
                </>
              );
              return c.to ? (
                <Link key={c.label} to={c.to} className="card-obsidian p-6 block hover:border-gold/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{body}</Link>
              ) : (
                <div key={c.label} className="card-obsidian p-6">{body}</div>
              );
            })}
          </div>
        </section>
      )}

      {summary.length > 0 && (
        <section aria-labelledby="summary-title">
          <h2 id="summary-title" className="text-lg font-semibold mb-3">Clientes hoy</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {summary.map((c) => {
              const body = (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">{c.label}</span>
                    <c.icon className={`w-5 h-5 ${c.color}`} aria-hidden="true" />
                  </div>
                  <p className="text-3xl font-serif font-medium text-slate-900">{fmtNum(c.value)}</p>
                  <p className="text-xs text-slate-600 mt-2">{c.help}</p>
                </>
              );
              return c.to ? (
                <Link key={c.label} to={c.to} className="card-obsidian p-6 block hover:border-gold/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{body}</Link>
              ) : (
                <div key={c.label} className="card-obsidian p-6">{body}</div>
              );
            })}
          </div>
        </section>
      )}

      {perms.includes("users") && (
        <section className="card-obsidian p-6" aria-labelledby="tiers-title">
          <h2 id="tiers-title" className="text-lg font-semibold">Clientes por nivel</h2>
          <p className="text-xs text-slate-600 mb-5">El nivel alcanzado en el año: no baja al canjear.</p>
          <div className="space-y-4">
            {Object.entries(stats.tier_distribution).map(([tier, count]: any) => (
              <div key={tier}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-slate-700">{tier}</span>
                  <span className="text-slate-600 font-mono">{plural(count, "cliente", "clientes")}</span>
                </div>
                <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden" aria-hidden="true">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(count / maxTier) * 100}%`, background: tierColors[tier] || "#64748B" }} />
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
